import { useLayoutEffect } from 'react';
import { lockPageScroll } from '../utils/pageLock';
import { isPWA } from '../utils/installPrompt';

/**
 * The browser chrome around a full-screen new-experience screen (People, the
 * Intro Card's hosts), in a Safari tab on iOS 26. Device-proven 2026-09-28/29
 * (b/signup-card-engine, b/signup-people; CLAUDE-PROJECT § Known Gotchas):
 *   · the strip behind the clock takes the ROOT background (html + body) —
 *     never a React container's, never theme-color;
 *   · at scroll 0 that strip is a flat colour; once the page is scrolled, the
 *     content above the viewport shows behind the clock instead (60pt on an
 *     874pt iPhone, and Safari reports the inset as 0);
 *   · so the page is locked (overflow hidden + the touch guard) and HELD at one
 *     scroll: 0 for a flat strip (People), SAFARI_BLEED for a picture that runs
 *     behind the clock (the card, which grows upward by the same amount).
 * Everything is restored on the way out; other screens rely on the default
 * white and a scrolling page. The home-screen app is a different mechanism
 * (`black-translucent`, switch-on day), so it never bleeds here.
 *
 * @param {Object} o
 * @param {string|null} o.colour - the root background: what Safari paints behind the
 *   clock; null leaves it to the screen (Detail sets it from its hero)
 * @param {number} [o.holdAt=0] - the page scroll to hold (0, or safariBleed())
 * @param {boolean} [o.lockTouches=true] - the touch guard. Pass false while a
 *   borrowed full-screen overlay with text fields is up (Quick Add): iOS nudges
 *   the page to make room for the keyboard, and the guard + scroll reset
 *   fighting that can land the tap on the overlay's backdrop, which closes it.
 * @param {boolean} [o.active=true] - the screen is up
 */
const useShellChrome = ({ colour, holdAt = 0, lockTouches = true, active = true }) => {
  // Before paint: when one screen hands the page to another (People back from
  // a Detail it opened, DETAIL §9a), the root colour and the held scroll
  // change in the same frame as the screens — after paint, People could show
  // one frame shifted by Detail's 64 px Safari hold (a blip on settle).
  useLayoutEffect(() => {
    if (!active) return undefined;
    const { documentElement: html, body } = document;
    // colour null: the screen paints the root itself (Detail, from its hero),
    // so it is neither set nor restored here — one owner per property.
    const paints = colour != null;
    const before = [
      html.style.backgroundColor,
      body.style.backgroundColor,
      html.style.overflow,
      body.style.overflow,
    ];
    if (paints) {
      html.style.backgroundColor = colour;
      body.style.backgroundColor = colour;
    }
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    window.scrollTo(0, holdAt);
    return () => {
      [html.style.overflow, body.style.overflow] = before.slice(2);
      if (paints) [html.style.backgroundColor, body.style.backgroundColor] = before;
    };
  }, [active, colour, holdAt]);

  // The guard swaps in the same phase: left to after paint, Detail's backstop
  // was still listening when People reset the page to 0 and put it back at
  // 64 for one frame — the blip as People settles (caught frame by frame,
  // WebKit, 2026-10-14).
  useLayoutEffect(() => {
    if (!active || !lockTouches) return undefined;
    return lockPageScroll(holdAt);
  }, [active, lockTouches, holdAt]);
};

// >= the status-bar height of current iPhones (60 measured on an 874pt screen;
// the home-screen app reports 62 there). A build call; the precision pass owns it.
export const SAFARI_BLEED = 64;

/** How far a picture should run up behind the clock: SAFARI_BLEED in a Safari tab, 0 in the home-screen app. */
export const safariBleed = () => (isPWA() ? 0 : SAFARI_BLEED);

export default useShellChrome;
